/**
 * WorkspaceHeader - Header du workspace POS unifié
 *
 * Affiche les onglets de navigation (POS, Préparation, Live)
 * avec des indicateurs visuels pour l'état du live et les commandes en attente
 *
 * Le filtrage des onglets est basé sur:
 * 1. Le rôle du vendeur (ADMIN/MANAGER ont accès complet aux workstations)
 * 2. Les workstations assignées au vendeur (POS, PREPARATION, LIVE)
 *
 * Le bouton Dashboard est uniquement visible pour les ADMIN.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react';
import { usePOSStore, WORKSTATION_CODES } from '../../hooks/usePOSStore';
import { useLiveShoppingStore } from '../../hooks/useLiveShoppingStore';
import { hasFullAccessRole } from '../../types/pos.types';
import { OfflineIndicator } from './OfflineIndicator';
import HardwareStatusIndicator from './HardwareStatusIndicator';
import { useClock } from '../../hooks/useClock';
import { IS_TOUCH_DEVICE } from '../../utils/touchDevice';
import { useHardwareStatus } from '../../hooks/useHardwareStatus';
import { usePendingSalesCount } from '../../contexts/OfflineContext';

export type WorkspaceTab = 'pos' | 'preparation' | 'live' | 'chat';

// Mapping entre les onglets UI et les codes workstation
const TAB_TO_WORKSTATION: Record<WorkspaceTab, string> = {
  pos: WORKSTATION_CODES.POS,
  preparation: WORKSTATION_CODES.PREPARATION,
  live: WORKSTATION_CODES.LIVE,
  chat: WORKSTATION_CODES.CHAT,
};

interface WorkspaceHeaderProps {
  activeTab: WorkspaceTab;
  onTabChange: (tab: WorkspaceTab) => void;
  pendingOrdersCount?: number;
  unreadMessagesCount?: number;
  onRequestPinForAccess?: () => void;
  onOpenClosing?: () => void;
  /** Rouvrir la liste des produits à mettre de côté (elle se ferme sur acquittement). */
  onOuvrirMisesDeCote?: () => void;
  misesDeCoteCount?: number;
}

export default function WorkspaceHeader({
  activeTab,
  onTabChange,
  pendingOrdersCount = 0,
  unreadMessagesCount = 0,
  onRequestPinForAccess,
  onOpenClosing,
  onOuvrirMisesDeCote,
  misesDeCoteCount = 0,
}: WorkspaceHeaderProps) {
  const navigate = useNavigate();
  const vendorName = usePOSStore((state) => state.currentVendorName);
  const vendorRole = usePOSStore((state) => state.currentVendorRole);
  const hasFullAccess = usePOSStore((state) => state.hasFullAccess);
  const hasWorkstationAccess = usePOSStore((state) => state.hasWorkstationAccess);

  const { sessionStatus, customersInQueue } = useLiveShoppingStore();
  const clock = useClock();

  // Extinction de la caisse (fin de journée) — via le Hardware Bridge local
  const { shutdownPos, musicAvailable, openMusic } = useHardwareStatus();
  const pendingSalesCount = usePendingSalesCount();

  const handleShutdown = async () => {
    // Jamais éteindre avec des ventes hors-ligne non synchronisées : elles
    // vivent dans l'IndexedDB de CE navigateur — la clôture les perdrait de vue.
    if (pendingSalesCount > 0) {
      alert(
        `${pendingSalesCount} vente(s) hors-ligne en attente de synchronisation.\n` +
          `Attends la synchronisation (indicateur en haut) avant d'éteindre.`,
      );
      return;
    }
    if (!window.confirm('Éteindre la caisse ?\n\nLe Mac va s\'éteindre complètement (fin de journée).')) return;
    const ok = await shutdownPos();
    if (!ok) {
      alert("Extinction impossible : bridge injoignable.\nUtilise le menu  → Éteindre.");
    }
  };

  // Déterminer si le vendeur peut accéder aux fonctionnalités étendues (basé sur le rôle)
  const canAccessExtendedFeatures = useMemo(() => {
    if (!vendorRole) return false;
    return hasFullAccessRole(vendorRole);
  }, [vendorRole]);

  // Vérifier l'accès à chaque onglet basé sur les workstations assignées
  const canAccessTab = (tab: WorkspaceTab): boolean => {
    const workstationCode = TAB_TO_WORKSTATION[tab];
    return hasWorkstationAccess(workstationCode as any);
  };

  // Classe CSS pour le bouton Live selon l'état
  const getLiveButtonClasses = () => {
    const baseClasses = 'px-5 py-2 min-h-[52px] rounded-lg text-lg font-medium transition-all flex items-center gap-2';

    if (activeTab === 'live') {
      return `${baseClasses} bg-blue-600 text-white`;
    }

    // Client en attente - style urgent avec animation
    if (customersInQueue > 0 || sessionStatus === 'customer_requesting') {
      return `${baseClasses} bg-red-500 text-white animate-pulse-live active:bg-red-600`;
    }

    switch (sessionStatus) {
      case 'idle':
        return `${baseClasses} bg-gray-100 text-gray-600 active:bg-gray-200`;
      case 'waiting':
        return `${baseClasses} bg-yellow-100 text-yellow-700 active:bg-yellow-200`;
      case 'active':
        return `${baseClasses} bg-red-500 text-white active:bg-red-600`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-600 active:bg-gray-200`;
    }
  };

  // Icône pour le bouton Live
  const getLiveIcon = () => {
    // Client en attente - icône rouge clignotante
    if (customersInQueue > 0) {
      return '🔴';
    }

    switch (sessionStatus) {
      case 'idle':
        return '⚫';
      case 'waiting':
        return '🟡';
      case 'customer_requesting':
      case 'active':
        return '🔴';
      default:
        return '⚫';
    }
  };

  // Handler pour clic sur onglet avec vérification d'accès
  const handleTabClick = (tab: WorkspaceTab) => {
    // Vérifier si le vendor a accès à cette workstation
    if (!canAccessTab(tab)) {
      return;
    }

    // POS est toujours accessible directement si le vendor y a accès
    if (tab === 'pos') {
      onTabChange(tab);
      return;
    }

    // Pour Préparation et Live, vérifier l'accès complet (rôle + PIN si nécessaire)
    if (!canAccessExtendedFeatures) {
      // CASHIER n'a pas accès aux fonctionnalités étendues même avec workstation assignée
      return;
    }

    if (hasFullAccess) {
      // Accès déjà validé (ADMIN ou PIN vérifié)
      onTabChange(tab);
    } else {
      // Demander le PIN
      onRequestPinForAccess?.();
    }
  };

  return (
    <header className={IS_TOUCH_DEVICE
      ? 'bg-white border-b border-gray-200 px-2 py-2 flex items-center justify-between gap-2 flex-wrap'
      : 'bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between'}>
      {/* Vendeur actuel */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
          <span className="text-2xl">
            {vendorRole === 'MANAGER' || vendorRole === 'ADMIN' ? '👔' :
             vendorRole === 'CASHIER_PLUS' ? '⭐' :
             vendorRole === 'FIELD' ? '🌱' : '👤'}
          </span>
          <div className="flex flex-col">
            <span className="font-semibold text-gray-800">{vendorName || 'Aucun vendeur'}</span>
            {vendorRole && (
              <span className="text-xs text-gray-500">
                {vendorRole === 'ADMIN' ? 'Admin' :
                 vendorRole === 'MANAGER' ? 'Manager' :
                 vendorRole === 'CASHIER_PLUS' ? 'Vendeur+' :
                 vendorRole === 'FIELD' ? 'Terrain' : 'Caissier'}
              </span>
            )}
          </div>
        </div>

        {/* Indicateur d'accès complet */}
        {hasFullAccess && canAccessExtendedFeatures && (
          <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
            Accès complet
          </div>
        )}
      </div>

      {/* Onglets de navigation */}
      <div className="flex items-center gap-2">
        {/* Onglet POS - visible si le vendor a accès à la workstation POS */}
        {canAccessTab('pos') && (
          <button
            onClick={() => handleTabClick('pos')}
            className={`px-5 py-2 min-h-[52px] rounded-lg text-lg font-medium transition-all flex items-center gap-2 ${
              activeTab === 'pos'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 active:bg-gray-200'
            }`}
          >
            🛒 POS
          </button>
        )}

        {/* Onglet Préparation - visible si le vendor a accès à la workstation PREPARATION */}
        {canAccessTab('preparation') && (
          <button
            onClick={() => handleTabClick('preparation')}
            disabled={!canAccessExtendedFeatures}
            className={`px-5 py-2 min-h-[52px] rounded-lg text-lg font-medium transition-all flex items-center gap-2 ${
              activeTab === 'preparation'
                ? 'bg-blue-600 text-white'
                : canAccessExtendedFeatures
                  ? 'bg-gray-100 text-gray-600 active:bg-gray-200'
                  : 'bg-gray-50 text-gray-300 cursor-not-allowed'
            }`}
          >
            📦 Prépa
            {pendingOrdersCount > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {pendingOrdersCount}
              </span>
            )}
          </button>
        )}

        {/* Onglet Live - visible si le vendor a accès à la workstation LIVE */}
        {canAccessTab('live') && (
          <button
            onClick={() => handleTabClick('live')}
            disabled={!canAccessExtendedFeatures}
            className={
              !canAccessExtendedFeatures
                ? 'px-5 py-2 min-h-[52px] rounded-lg text-lg font-medium bg-gray-50 text-gray-300 cursor-not-allowed flex items-center gap-2'
                : getLiveButtonClasses()
            }
          >
            {canAccessExtendedFeatures ? getLiveIcon() : '⚫'} Live
            {customersInQueue > 0 && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                activeTab === 'live'
                  ? 'bg-white text-blue-600'
                  : 'bg-white text-red-600 animate-pulse'
              }`}>
                {customersInQueue}
              </span>
            )}
          </button>
        )}

        {/* Menu Gestion — regroupe Stocks, Pertes, Réappro, Clôture */}
        {(hasWorkstationAccess(WORKSTATION_CODES.STOCKS) || (hasWorkstationAccess(WORKSTATION_CODES.CLOSING) && onOpenClosing)) && (
          <Menu as="div" className="relative">
            <MenuButton className="px-5 py-2 min-h-[52px] rounded-lg text-lg font-medium transition-all flex items-center gap-2 bg-gray-100 text-gray-600 active:bg-gray-200">
              📊 Gestion ▾
            </MenuButton>
            <MenuItems className="absolute left-0 top-full mt-1 z-50 bg-white rounded-xl shadow-lg border border-gray-200 py-2 w-72">
              {hasWorkstationAccess(WORKSTATION_CODES.STOCKS) && (
                <MenuItem>
                  <button
                    onClick={() => navigate('/stocks')}
                    className="w-full text-left px-5 py-3 min-h-[56px] text-base font-medium text-gray-700 data-[focus]:bg-gray-100 flex items-center gap-2"
                  >
                    📊 Stocks
                  </button>
                </MenuItem>
              )}
              {hasWorkstationAccess(WORKSTATION_CODES.STOCKS) && (
                <MenuItem>
                  <button
                    onClick={() => navigate('/pos/restock-alerts')}
                    className="w-full text-left px-5 py-3 min-h-[56px] text-base font-medium text-amber-700 data-[focus]:bg-amber-50 flex items-center gap-2"
                  >
                    ⚠️ Réappro
                  </button>
                </MenuItem>
              )}
              {hasWorkstationAccess(WORKSTATION_CODES.STOCKS) && (
                <MenuItem>
                  <button
                    onClick={() => navigate('/pos/reception')}
                    className="w-full text-left px-5 py-3 min-h-[56px] text-base font-medium text-blue-700 data-[focus]:bg-blue-50 flex items-center gap-2"
                  >
                    📥 Réception
                  </button>
                </MenuItem>
              )}
              {onOuvrirMisesDeCote && (
                <MenuItem>
                  <button
                    onClick={onOuvrirMisesDeCote}
                    className="w-full text-left px-5 py-3 min-h-[56px] text-base font-medium text-sky-700 data-[focus]:bg-sky-50 flex items-center gap-2"
                  >
                    🧊 Mises de côté
                    {misesDeCoteCount > 0 && (
                      <span className="ml-auto text-xs font-bold bg-sky-600 text-white rounded-full px-2 py-0.5">
                        {misesDeCoteCount}
                      </span>
                    )}
                  </button>
                </MenuItem>
              )}
              {hasWorkstationAccess(WORKSTATION_CODES.CLOSING) && onOpenClosing && (
                <MenuItem>
                  <button
                    onClick={onOpenClosing}
                    className="w-full text-left px-5 py-3 min-h-[56px] text-base font-medium text-purple-700 data-[focus]:bg-purple-50 flex items-center gap-3 border-t border-gray-100"
                  >
                    📋 Clôture
                  </button>
                </MenuItem>
              )}
              <MenuItem>
                <button
                  onClick={() => void handleShutdown()}
                  className="w-full text-left px-5 py-3 min-h-[56px] text-base font-medium text-red-700 data-[focus]:bg-red-50 flex items-center gap-3 border-t border-gray-100"
                >
                  ⏻ Éteindre la caisse
                </button>
              </MenuItem>
            </MenuItems>
          </Menu>
        )}

        {/* Musique du magasin — seulement sur les caisses dont le bridge le
            déclare (POS-01/02). Ouvre l'app Musique ; retour par Safari dans le Dock. */}
        {musicAvailable && (
          <button
            onClick={async () => {
              if (!(await openMusic())) alert("Impossible d'ouvrir Musique : bridge injoignable.");
            }}
            className="px-5 py-2 min-h-[52px] rounded-lg text-lg font-medium transition-all flex items-center gap-2 bg-gray-100 text-gray-600 active:bg-gray-200"
            title="Ouvrir Musique — pour revenir à la caisse, touche Safari dans le Dock"
          >
            🎵 Musique
          </button>
        )}

        {/* Onglet Chat - visible si le vendor a accès à la workstation CHAT */}
        {canAccessTab('chat') && (
          <button
            onClick={() => handleTabClick('chat')}
            disabled={!canAccessExtendedFeatures}
            className={`px-5 py-2 min-h-[52px] rounded-lg text-lg font-medium transition-all flex items-center gap-2 ${
              activeTab === 'chat'
                ? 'bg-blue-600 text-white'
                : canAccessExtendedFeatures
                  ? unreadMessagesCount > 0
                    ? 'bg-green-100 text-green-700 active:bg-green-200'
                    : 'bg-gray-100 text-gray-600 active:bg-gray-200'
                  : 'bg-gray-50 text-gray-300 cursor-not-allowed'
            }`}
          >
            💬 Chat
            {unreadMessagesCount > 0 && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                activeTab === 'chat'
                  ? 'bg-white text-blue-600'
                  : 'bg-red-500 text-white animate-pulse'
              }`}>
                {unreadMessagesCount}
              </span>
            )}
          </button>
        )}
      </div>

      {/* Section droite : Horloge + Hardware + Offline + Dashboard */}
      <div className="flex items-center gap-3">
        {/* Horloge */}
        <span className="text-lg font-mono font-bold text-gray-700">{clock}</span>

        {/* Indicateur Hardware */}
        <HardwareStatusIndicator />

        {/* Indicateur Offline */}
        <OfflineIndicator />

        {/* Bouton Dashboard - visible si accès workstation DASHBOARD */}
        {(vendorRole === 'ADMIN' || hasWorkstationAccess('DASHBOARD' as any)) && (
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 min-h-[52px] text-gray-600 active:text-gray-800 active:bg-gray-100 rounded-lg transition-all text-base"
          >
            ← Dashboard
          </button>
        )}
      </div>

      {/* Style pour l'animation du bouton Live */}
      <style>{`
        @keyframes pulse-live {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
          }
          50% {
            box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
          }
        }
        .animate-pulse-live {
          animation: pulse-live 1.5s infinite;
        }
      `}</style>
    </header>
  );
}
